/**
 * Video component that starts playing as soon as it is scrolled into view.
 * @module Video Scroll Play
 *
 * @version 1.0
 */

import React from 'react'
import { Waypoint } from 'react-waypoint'
import { Progress } from 'semantic-ui-react'

require('./styles.scss')

/**
 * Video component, starts playing as soon as it strolls into view.
 * @class
 * @extends React.PureComponent
 *
 */
export default class VideoScrollPlay extends React.PureComponent {
  // region React Component implementation

  /**
   * @param {string} props.mp4 - URL of MP4 version of video.
   * @param {string} props.webm - URL of WEBM version of video.
   * @param {string} props.ogv - URL of OGG version of video.
   * @param {string} props.poster - URL to image source for the placeholder
   * image.
   */
  constructor(props) {
    super(props)

    this.videoElement = null
    this.isMobile = window.innerWidth < 600
    this.ua = window.navigator.userAgent
    this.isAndroidNativeApp = /app-android-smartphone/.test(this.ua)

    this.state = {
      paused: true,
      muted: true,
      playheadPosition: 0,
      duration: 0,
      durationString: ''
    }

    this.setStartingPoint = this.setStartingPoint.bind(this)
    this.WaypointEnterHandler = this.WaypointEnterHandler.bind(this)
    this.WaypointLeaveHandler = this.WaypointLeaveHandler.bind(this)
    this.PlayButtonHandler = this.PlayButtonHandler.bind(this)
    this.PauseButtonHandler = this.PauseButtonHandler.bind(this)
    this.MuteButtonHandler = this.MuteButtonHandler.bind(this)
    this.togglePlayState = this.togglePlayState.bind(this)
    this.rewindHandler = this.rewindHandler.bind(this)
    this.setDuration = this.setDuration.bind(this)
    this.setPlayhead = this.setPlayhead.bind(this)
    this.renderPlayButton = this.renderPlayButton.bind(this)
    this.updatePlayStatus = this.updatePlayStatus.bind(this)
  }

  render() {
    let buttonsClasses = `${
      this.isMobile ? 'bottom attached basic' : 'bottom attached basic'
    } controls ui teal icon large buttons`

    return (
      <Waypoint
        onEnter={this.WaypointEnterHandler}
        onLeave={this.WaypointLeaveHandler}
        scrollableAncestor={window}
      >
        <div className='video-auto-play' style={{ position: 'relative' }}>
          <video
            playsInline
            loop
            muted
            preload={
              this.props.allowAutoPlay || !this.isMobile ? 'auto' : 'metadata'
            }
            onPlay={this.setStartingPoint}
            onDurationChange={this.setDuration}
            onTimeUpdate={this.setPlayhead}
            onEnded={this.updatePlayStatus}
            poster={this.props.poster}
            ref={video => {
              if (video) {
                video.setAttribute('playsinline', '')
                this.videoElement = video
              }
            }}
            onClick={this.MuteButtonHandler}
          >
            {this.props.webm && !this.isMobile && (
              <source src={this.props.webm} type='video/webm' />
            )}
            {this.props.ogv && !this.isMobile && (
              <source src={this.props.ogv} type='video/ogg' />
            )}
            {this.props.mp4 && !this.isMobile && (
              <source src={this.props.mp4} type='video/mp4' />
            )}

            {this.props.webmMobile && this.isMobile && (
              <source src={this.props.webmMobile} type='video/webm' />
            )}
            {this.props.ogvMobile && this.isMobile && (
              <source src={this.props.ogvMobile} type='video/ogg' />
            )}
            {this.props.mp4Mobile && this.isMobile && (
              <source src={this.props.mp4Mobile} type='video/mp4' />
            )}
          </video>
          {this.isMobile && (
            <div className='source'>
              Videos: <a href={this.props.originals}>SRF</a>
            </div>
          )}
          {!this.isMobile && (
            <div className='source'>
              Videos: <a href={this.props.originals}>SRF Archiv</a>
            </div>
          )}

          {this.props.showControls && (
            <Progress
              value={this.state.playheadPosition}
              total={this.state.duration}
              attached='bottom'
              color='teal'
            />
          )}

          {this.props.showControls && this.state.paused && (
            <div className='ui top left attached small inverted teal label'>
              <i className='time icon' /> {this.state.durationString}
            </div>
          )}

          {this.props.showControls && (
            <div className={buttonsClasses}>
              {!this.props.silent && (
                <a
                  onClick={this.MuteButtonHandler}
                  className='ui icon toggle button'
                >
                  {this.state.muted && (
                    <i className='icons'>
                      {' '}
                      <i className='volume up icon' />
                      <i className='dont red corner icon' />
                    </i>
                  )}{' '}
                  {!this.state.muted && <i className='volume up icon' />}
                </a>
              )}
            </div>
          )}
        </div>
      </Waypoint>
    )
  }

  // endregion

  // region Event Handler

  setStartingPoint() {
    if (this.props.startingPoint) {
      this.videoElement.currentTime = this.props.startingPoint
    }
  }

  setDuration() {
    let dateObject = new Date(null)
    dateObject.setSeconds(this.videoElement.duration)

    this.setState({
      durationString: dateObject.toTimeString().substr(3, 5),
      duration: this.videoElement.duration
    })
  }

  setPlayhead() {
    this.setState({
      playheadPosition: this.videoElement.currentTime
    })
  }

  WaypointEnterHandler() {
    // console.log('typedelay: ', this.typeDelay)
    if (this.props.allowAutoPlay || !this.isMobile) {
      this.videoElement.currentTime = 0
      this.videoElement.muted = true
      this.videoElement.play()
      this.setState({
        paused: this.videoElement.paused,
        muted: true
      })
    }
  }

  WaypointLeaveHandler() {
    // console.log('LEEEEEEEEEAVE....')
    this.videoElement.pause()
  }

  PlayButtonHandler() {
    this.videoElement.muted = false
    this.videoElement.currentTime = 0
    this.videoElement.play()
  }

  PauseButtonHandler() {
    this.videoElement.pause()
  }

  MuteButtonHandler() {
    this.videoElement.muted = !this.videoElement.muted
    this.setState({
      muted: this.videoElement.muted
    })
  }

  togglePlayState() {
    if (!this.videoElement.paused) {
      this.videoElement.pause()
    } else {
      this.videoElement.play()
      this.videoElement.muted = false
    }

    this.setState({
      paused: this.videoElement.paused,
      muted: this.videoElement.muted
    })
  }

  rewindHandler() {
    this.videoElement.currentTime = 0
  }

  updatePlayStatus() {
    this.setState({
      paused: this.videoElement.paused,
      muted: this.videoElement.muted
    })
  }

  // endregion

  // region Render functions
  renderPlayButton() {
    let symbol = this.state.paused ? 'play' : 'pause'
    if (
      this.state.paused &&
      this.state.playheadPosition === this.state.duration
    ) {
      symbol = 'undo'
    }

    return (
      <a onClick={this.togglePlayState} className='ui icon button'>
        {' '}
        <i className={`${symbol} icon`} />{' '}
      </a>
    )
  }

  // endregion
}

VideoScrollPlay.defaultProps = {
  showControls: false,
  silent: true,
  allowAutoPlay: true
}
